import type { ReactNode } from "react";

interface IconProps {
  className?: string;
}

function IconBase({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function AntIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="14" r="2" />
      <path d="M8 10l-2-4M16 10l2-4M10 8h4M7 14l-3 4M17 14l3 4M9 18h6" />
    </IconBase>
  );
}

export function RodentIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="14" cy="13" rx="5" ry="4" />
      <circle cx="18" cy="11" r="1.5" fill="currentColor" stroke="none" />
      <path d="M9 13c-2 0-4 1-5 3M6 10l-2-1" />
    </IconBase>
  );
}

export function SpiderIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="10" r="3" />
      <path d="M12 7V4M12 13v7M8 9L5 6M16 9l3-3M7 12H4M17 12h3M8.5 14.5L6 18M15.5 14.5L18 18" />
    </IconBase>
  );
}

export function CockroachIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="13" rx="6" ry="4" />
      <path d="M6 11l-3-2M18 11l3-2M6 15l-2 3M18 15l2 3" />
    </IconBase>
  );
}

export function TermiteIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M6 16c0-4 2.5-7 6-7s6 3 6 7" />
      <path d="M8 16h8M10 12v4M14 12v4" />
    </IconBase>
  );
}

export function CricketIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M8 14c2-3 6-3 8 0M6 16l-2 2M18 16l2 2" />
      <path d="M10 10l-1-3M14 10l1-3" />
    </IconBase>
  );
}

export function EarwigIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="13" rx="3" ry="5" />
      <path d="M9 8l-2-3M15 8l2-3M9 18l-1 3M15 18l1 3" />
    </IconBase>
  );
}

export function WaspIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="14" rx="3" ry="5" />
      <path d="M10 9l-2-4M14 9l2-4M8 14h8" />
      <path d="M6 6l2 2M18 6l-2 2" />
    </IconBase>
  );
}

export function BedBugIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="14" rx="5" ry="3" />
      <path d="M7 14h10M9 11h6" />
    </IconBase>
  );
}

export function RaccoonIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="9" cy="10" r="2" />
      <circle cx="15" cy="10" r="2" />
      <path d="M12 12c-3 0-5 2-5 5h10c0-3-2-5-5-5z" />
    </IconBase>
  );
}

export function SnakeIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M6 18c0-4 3-6 6-6s6 2 6 6" />
      <circle cx="8" cy="8" r="2" />
    </IconBase>
  );
}

export function WildAnimalIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M8 16c0-4 2-8 4-8s4 4 4 8" />
      <path d="M6 10l-2-2M18 10l2-2M10 6l-1-3M14 6l1-3" />
    </IconBase>
  );
}

export function HomeIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M3 12l9-8 9 8" />
      <path d="M5 10v9h14v-9" />
    </IconBase>
  );
}

export function BuildingIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <rect x="4" y="4" width="16" height="18" rx="1" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
    </IconBase>
  );
}

export function CheckIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l3 3 5-6" />
    </IconBase>
  );
}

export function AlertIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v5M12 16h.01" />
    </IconBase>
  );
}

export function PhoneIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M6.5 4h3l1.5 4-2 1.5c1 2.5 3 4.5 5.5 5.5L18 13l4 1.5v3C16 18 6 14 6.5 4z" />
    </IconBase>
  );
}

export function LocationIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M12 21s6-5.2 6-10a6 6 0 10-12 0c0 4.8 6 10 6 10z" />
      <circle cx="12" cy="11" r="2" />
    </IconBase>
  );
}

const PEST_ICONS: Record<string, (props: IconProps) => ReactNode> = {
  ants: AntIcon,
  "mice-rats": RodentIcon,
  spiders: SpiderIcon,
  cockroaches: CockroachIcon,
  termites: TermiteIcon,
  crickets: CricketIcon,
  "earwigs-silverfish": EarwigIcon,
  "wasps-bees": WaspIcon,
  "bed-bugs": BedBugIcon,
  "raccoons-squirrels": RaccoonIcon,
  snakes: SnakeIcon,
  "wild-animals": WildAnimalIcon,
};

export function PestIcon({ id, className }: { id: string; className?: string }) {
  const Icon = PEST_ICONS[id];
  if (!Icon) return null;
  return <Icon className={className} />;
}
